import {
  DEFAULT_PREFS,
  GENDERS,
  GENDER_FILTERS,
  RADIUS_KM,
  SCOPES,
  type Gender,
  type SearchPrefs,
} from '@repo/shared/domain';
import { readJson, writeJson } from './storage.ts';

export interface Settings {
  gender: Gender | null;
  prefs: SearchPrefs;
  /** "Ich bin mindestens 18 Jahre alt" bestätigt */
  adultConfirmed: boolean;
  /** Zuletzt gewählte Kamera und zuletzt gewähltes Mikrofon */
  devices: { cameraId: string | null; micId: string | null };
  /** Kurzer Ton, sobald jemand da ist */
  sound: boolean;
}

const KEY = 'app.settings.v1';

const isOneOf = <T extends string>(values: readonly T[], value: unknown): value is T =>
  typeof value === 'string' && (values as readonly string[]).includes(value);

export function loadSettings(): Settings {
  const stored = readJson<Record<string, unknown>>('local', KEY) ?? {};
  const prefs = (stored.prefs ?? {}) as Record<string, unknown>;
  const radius = Number(prefs.radiusKm);
  const devices = (stored.devices ?? {}) as Record<string, unknown>;
  const deviceId = (value: unknown): string | null =>
    typeof value === 'string' && value !== '' ? value : null;

  return {
    gender: isOneOf(GENDERS, stored.gender) ? stored.gender : null,
    prefs: {
      scope: isOneOf(SCOPES, prefs.scope) ? prefs.scope : DEFAULT_PREFS.scope,
      radiusKm:
        Number.isInteger(radius) && radius >= RADIUS_KM.min && radius <= RADIUS_KM.max
          ? radius
          : DEFAULT_PREFS.radiusKm,
      genderFilter: isOneOf(GENDER_FILTERS, prefs.genderFilter)
        ? prefs.genderFilter
        : DEFAULT_PREFS.genderFilter,
    },
    adultConfirmed: stored.adultConfirmed === true,
    devices: { cameraId: deviceId(devices.cameraId), micId: deviceId(devices.micId) },
    sound: stored.sound !== false,
  };
}

export function saveSettings(settings: Settings): void {
  writeJson('local', KEY, settings);
}
